import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../users/userform.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiArrowLeft } from "react-icons/fi";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import DateInput from "../components/Forms/DateInput";
import { createVehicleInsurance, updateVehicleInsurance, getVehicleInsurances } from "../../services/VehicleService";

const VehicleInsuranceForm = () => {
  const navigate = useNavigate();
  const { vehicleId, insuranceId } = useParams();
  const isEditMode = !!insuranceId;

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);

  const [formData, setFormData] = useState({
    insurance_start_date: "",
    insurance_expiry_date: "",
    insurance_provider_name: "",
    insurance_amount: "",
    is_current: true
  });

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000 });

  useEffect(() => {
    if (isEditMode) {
      fetchInsuranceData();
    }
  }, [vehicleId, insuranceId]);

  const fetchInsuranceData = async () => {
    try {
      setLoading(true);
      const res = await getVehicleInsurances(vehicleId);
      const insurances = res.data.data || [];
      const insurance = insurances.find(ins => ins.id === parseInt(insuranceId));
      
      if (insurance) {
        setFormData({
          insurance_start_date: insurance.insurance_start_date ? new Date(insurance.insurance_start_date).toISOString().split('T')[0] : "",
          insurance_expiry_date: insurance.insurance_expiry_date ? new Date(insurance.insurance_expiry_date).toISOString().split('T')[0] : "",
          insurance_provider_name: insurance.insurance_provider_name || "",
          insurance_amount: insurance.insurance_amount?.toString() || "",
          is_current: insurance.is_current !== undefined ? insurance.is_current : true
        });
      } else {
        notifyError("Insurance not found");
        navigate(`/vehicle/vehicleDetails/${vehicleId}`);
      }
      setDataLoaded(true);
    } catch (error) {
      console.error("Insurance fetch error:", error);
      notifyError(error?.response?.data?.message || "Failed to load insurance data");
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.insurance_start_date) {
      errs.insurance_start_date = "Start date is required";
    }
    if (!formData.insurance_expiry_date) {
      errs.insurance_expiry_date = "Expiry date is required";
    }
    if (formData.insurance_start_date && formData.insurance_expiry_date) {
      const start = new Date(formData.insurance_start_date);
      const expiry = new Date(formData.insurance_expiry_date);
      if (expiry <= start) {
        errs.insurance_expiry_date = "Expiry date must be after start date";
      }
    }
    if (formData.insurance_provider_name.trim() && !/[a-zA-Z]/.test(formData.insurance_provider_name.trim())) {
      errs.insurance_provider_name = "Provider name must contain at least one letter";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      notifyError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    try {
      const payload = {
        insurance_start_date: formData.insurance_start_date,
        insurance_expiry_date: formData.insurance_expiry_date,
        insurance_provider_name: formData.insurance_provider_name || null,
        insurance_amount: formData.insurance_amount ? parseFloat(formData.insurance_amount) : null,
        is_current: formData.is_current
      };

      if (isEditMode) {
        await updateVehicleInsurance(vehicleId, insuranceId, payload);
        notifySuccess("Insurance updated successfully");
      } else {
        await createVehicleInsurance(vehicleId, payload);
        notifySuccess("Insurance added successfully");
      }
      setTimeout(() => navigate(`/vehicle/vehicleDetails/${vehicleId}`), 1500);
    } catch (error) {
      console.error("Submit error:", error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'add'} insurance`);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!dataLoaded && loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => navigate(`/vehicle/vehicleDetails/${vehicleId}`)}
      >
        <FiArrowLeft style={{ marginRight: "0.5rem" }} />
        Back
      </button>

      <ToastContainer />

      <div className="card p-3 p-md-4 mt-2">
        <h4 className="mb-4">{isEditMode ? "Edit Insurance" : "Add Insurance"}</h4>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 col-md-6 mb-3">
              <label>Insurance Start Date*</label>
              <DateInput
                type="date"
                name="insurance_start_date"
                value={formData.insurance_start_date}
                onChange={handleChange}
                className={`form-control ${errors.insurance_start_date ? 'is-invalid' : ''}`}
                required
              />
              {errors.insurance_start_date && <div className="text-danger small">{errors.insurance_start_date}</div>}
            </div>

            <div className="col-12 col-md-6 mb-3">
              <label>Insurance Expiry Date*</label>
              <DateInput
                type="date"
                name="insurance_expiry_date"
                value={formData.insurance_expiry_date}
                onChange={handleChange}
                className={`form-control ${errors.insurance_expiry_date ? 'is-invalid' : ''}`}
                required
              />
              {errors.insurance_expiry_date && <div className="text-danger small">{errors.insurance_expiry_date}</div>}
            </div>

            <div className="col-12 col-md-6 mb-3">
              <label>Insurance Provider Name</label>
              <input
                name="insurance_provider_name"
                value={formData.insurance_provider_name}
                onChange={handleChange}
                className={`form-control ${errors.insurance_provider_name ? 'is-invalid' : ''}`}
                placeholder="Enter insurance provider name"
              />
              {errors.insurance_provider_name && <div className="text-danger small">{errors.insurance_provider_name}</div>}
            </div>

            <div className="col-12 col-md-6 mb-3">
              <label>Insurance Amount</label>
              <input
                type="number"
                name="insurance_amount"
                value={formData.insurance_amount}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter insurance amount"
                step="0.01"
              />
            </div>

            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_current"
                  id="is_current"
                  checked={formData.is_current}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="is_current">
                  Mark as Current Insurance
                </label>
                <small className="form-text text-muted d-block">
                  When checked, all other insurances for this vehicle will be marked as not current.
                </small>
              </div>
            </div>
          </div>

          <div className="toolbar toolbar-bottom p-2">
            <div className="d-flex flex-row justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => navigate(`/vehicle/vehicleDetails/${vehicleId}`)}
                disabled={isSubmitting}
                style={{ flex: '1', maxWidth: '200px', minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn user-form-btn sw-btn-next"
                style={{ flex: '1', maxWidth: '200px', minWidth: '100px' }}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : null}
                {isEditMode ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>

      <InternalFooter />
    </div>
  );
};

export default VehicleInsuranceForm;
